import React, { createContext, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { EncryptionProvider } from "common/crypto";
import KeycloakIDPAdapter from "common/Adapters/IDPAdapter/KeycloakIDPAdapter";

const initialSnapshot = EncryptionProvider.getSnapshot();

const defaultValue = {
  isMasterKeyValid: initialSnapshot.authenticated,
  isMasterKeyRestoring: false,
  cryptoSessionRevision: initialSnapshot.revision,
  cryptoEngineVersion: initialSnapshot.engineVersion,
  clearMasterKeySession: () => {},
  isLoggingOut: false,
  isMasterKeyModalShown: false,
  setIsMasterKeyModalShown: () => {},
};

export const MasterKeyContext = createContext(defaultValue);

const MasterKeyProvider = ({ children }) => {
  const [cryptoSession, setCryptoSession] = useState(() => EncryptionProvider.getSnapshot());
  const [isMasterKeyRestoring, setIsMasterKeyRestoring] = useState(() => !cryptoSession.authenticated && EncryptionProvider.hasStoredSession());
  const [isLoggingOut, setIsLoggingOut] = useState(() => Boolean(KeycloakIDPAdapter.isLogoutInProgress?.()));
  const [isMasterKeyModalShown, setIsMasterKeyModalShown] = useState(false);

  useEffect(() => EncryptionProvider.subscribe(setCryptoSession), []);

  useEffect(() => KeycloakIDPAdapter.subscribeLogoutState?.(setIsLoggingOut), []);

  useEffect(() => {
    if (isLoggingOut) setIsMasterKeyModalShown(false);
  }, [isLoggingOut]);

  useEffect(() => {
    if (EncryptionProvider.isInitialized() || !EncryptionProvider.hasStoredSession()) {
      setIsMasterKeyRestoring(false);
      return undefined;
    }

    let mounted = true;
    setIsMasterKeyRestoring(true);

    EncryptionProvider.restoreSession()
      .catch((error) => {
        // EncryptionProvider owns restoration cleanup. Purging here could destroy
        // a newer session that was activated while restoration was in flight.
        console.error("Failed to restore the master-key session", error);
      })
      .finally(() => {
        if (mounted) setIsMasterKeyRestoring(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const clearMasterKeySession = useCallback(() => EncryptionProvider.purge(), []);

  const updateMasterKeyModalVisibility = useCallback(
    (nextValue) => {
      setIsMasterKeyModalShown((current) => {
        const next = typeof nextValue === "function" ? nextValue(current) : nextValue;
        return isLoggingOut ? false : Boolean(next);
      });
    },
    [isLoggingOut]
  );

  return (
    <MasterKeyContext.Provider
      value={{
        isMasterKeyValid: cryptoSession.authenticated,
        isMasterKeyRestoring,
        cryptoSessionRevision: cryptoSession.revision,
        cryptoEngineVersion: cryptoSession.engineVersion,
        clearMasterKeySession,
        isLoggingOut,
        isMasterKeyModalShown,
        setIsMasterKeyModalShown: updateMasterKeyModalVisibility,
      }}
    >
      {children}
    </MasterKeyContext.Provider>
  );
};

MasterKeyProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default MasterKeyProvider;
